<template>
  <div
    ref="row"
    class="otp"
    :class="[state ? `otp--${state}` : '', { 'otp--merging': merging, 'otp--merged': merged }]"
  >
    <div
      v-for="(digit, i) in digits"
      :key="i"
      class="otp__box"
      :class="{ 'otp__box--filled': digit, 'otp__box--active': focused && !state && i === activeIndex }"
      :style="{ '--i': i, '--dx': offsets[i] || '0px' }"
      aria-hidden="true"
    >
      <span v-if="digit" :key="`${i}-${digit}`" class="otp__digit">{{ digit }}</span>
      <span v-else-if="focused && !state && i === activeIndex" class="otp__caret" />
    </div>
    <div class="otp__check" aria-hidden="true">
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor"
           stroke-width="2.8" stroke-linecap="round" stroke-linejoin="round">
        <path d="M5 12.5 10 17.5 19 7" />
      </svg>
    </div>
    <!-- One real field over the boxes: typing, backspace, paste and the
         browser's one-time-code autofill all work as on a plain input. The
         popup's attributes and listeners land here. -->
    <input
      ref="input"
      class="otp__input"
      type="text"
      inputmode="numeric"
      autocomplete="one-time-code"
      v-bind="$attrs"
      @focus="focused = true"
      @blur="focused = false"
    />
  </div>
</template>

<script>
// The six boxes of the email-code popups (LoginCodePopup, EmailCodePopup).
// `code` is what the boxes show; `state` drives the animation:
//   "verifying" — the digits jump in a wave;
//   "success"   — the boxes turn green, slide to the centre and become one
//                 square with a check (SUCCESS_MS in all);
//   "error"     — the boxes shake in red and the digits drop out.
export const OTP_SUCCESS_MS = 1300;

export default {
  name: "OtpCodeBoxes",
  inheritAttrs: false,
  props: {
    code: { type: String, default: "" },
    length: { type: Number, default: 6 },
    state: { type: String, default: "" },
  },
  data() {
    return { focused: false, merging: false, merged: false, offsets: [], timers: [] };
  },
  computed: {
    digits() {
      return Array.from({ length: this.length }, (_, i) => this.code[i] || "");
    },
    activeIndex() {
      return Math.min(this.code.length, this.length - 1);
    },
  },
  watch: {
    state(now) {
      this.clearTimers();
      this.merging = false;
      this.merged = false;
      if (now === "success") this.playSuccess();
    },
  },
  beforeUnmount() {
    this.clearTimers();
  },
  methods: {
    focus() {
      if (this.$refs.input) this.$refs.input.focus();
    },
    // Each box slides by its distance from the centre of the row.
    playSuccess() {
      const row = this.$refs.row;
      const mid = row ? row.clientWidth / 2 : 0;
      const boxes = row ? Array.from(row.querySelectorAll(".otp__box")) : [];
      this.offsets = boxes.map((b) => `${mid - (b.offsetLeft + b.offsetWidth / 2)}px`);
      this.timers.push(setTimeout(() => (this.merging = true), 550));
      this.timers.push(setTimeout(() => (this.merged = true), 1000));
    },
    clearTimers() {
      this.timers.forEach(clearTimeout);
      this.timers = [];
    },
  },
};
</script>

<style scoped>
.otp {
  --otp-accent: #489b79;
  --otp-accent-soft: #eaf5f0;
  --otp-error: #ef4444;
  --otp-error-soft: #fef2f2;
  --otp-line: #e5e7eb;
  --otp-line-strong: #9ca3af;
  position: relative;
  display: flex;
  justify-content: center;
  gap: 8px;
}
.otp__box {
  position: relative;
  width: 48px;
  height: 56px;
  border: 1.5px solid var(--otp-line);
  border-radius: 12px;
  background: #fff;
  display: grid;
  place-items: center;
  font-size: 24px;
  font-weight: 600;
  color: #000;
  font-variant-numeric: tabular-nums;
  transition: border-color 0.2s, background-color 0.25s, color 0.2s, box-shadow 0.2s;
}
.otp__box--filled { border-color: var(--otp-line-strong); }
.otp__box--active {
  border-color: var(--otp-accent);
  box-shadow: 0 0 0 4px rgb(72 155 121 / 0.15);
}
.otp__digit { display: inline-block; animation: otp-pop 0.28s cubic-bezier(0.3, 1.6, 0.5, 1); }
.otp__caret {
  width: 2px;
  height: 26px;
  border-radius: 1px;
  background: var(--otp-accent);
  animation: otp-blink 1s steps(1) infinite;
}
.otp__input {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  border: 0;
  padding: 0;
  font-size: 16px; /* no zoom on iOS */
  cursor: text;
}
.otp__input:disabled { cursor: default; }

/* verifying: a wave runs through the digits */
.otp--verifying .otp__box {
  border-color: var(--otp-accent);
  animation: otp-wave 1.1s ease-in-out infinite;
  animation-delay: calc(var(--i) * 90ms);
}

/* success: green one by one, then into the centre, then one square with a check */
.otp--success .otp__box {
  background: var(--otp-accent);
  border-color: var(--otp-accent);
  color: #fff;
  transition-delay: calc(var(--i) * 60ms);
}
.otp--merging .otp__box {
  color: transparent;
  transform: translateX(var(--dx)) scale(0.92);
  transition: transform 0.5s cubic-bezier(0.65, 0, 0.25, 1), color 0.12s;
}
.otp--merged .otp__box { opacity: 0; transition: opacity 0.1s; }
.otp__check {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 56px;
  height: 56px;
  margin: -28px 0 0 -28px;
  border-radius: 16px;
  background: var(--otp-accent);
  color: #fff;
  display: grid;
  place-items: center;
  opacity: 0;
  pointer-events: none;
}
.otp__check path { stroke-dasharray: 24; stroke-dashoffset: 24; }
.otp--merged .otp__check { opacity: 1; animation: otp-check-pop 0.5s cubic-bezier(0.3, 1.6, 0.5, 1) both; }
.otp--merged .otp__check path { animation: otp-draw 0.35s ease-out 0.18s forwards; }

/* error: shake in red, the digits drop out */
.otp--error { animation: otp-shake 0.45s cubic-bezier(0.36, 0.07, 0.19, 0.97); }
.otp--error .otp__box { border-color: var(--otp-error); background: var(--otp-error-soft); color: var(--otp-error); }
.otp--error .otp__digit { animation: otp-drop 0.35s ease-in 0.35s both; }

@keyframes otp-pop { 0% { transform: scale(0.6); opacity: 0.4; } 100% { transform: scale(1); opacity: 1; } }
@keyframes otp-blink { 50% { opacity: 0; } }
@keyframes otp-wave {
  0%, 60%, 100% { transform: translateY(0); background-color: #fff; }
  25% { transform: translateY(-10px); background-color: var(--otp-accent-soft); }
}
@keyframes otp-check-pop { 0% { transform: scale(0.6); } 60% { transform: scale(1.12); } 100% { transform: scale(1); } }
@keyframes otp-draw { to { stroke-dashoffset: 0; } }
@keyframes otp-shake {
  10%, 90% { transform: translateX(-2px); }
  20%, 80% { transform: translateX(4px); }
  30%, 50%, 70% { transform: translateX(-8px); }
  40%, 60% { transform: translateX(8px); }
}
@keyframes otp-drop { to { transform: translateY(10px); opacity: 0; } }

@media (max-width: 400px) {
  .otp { gap: 6px; }
  .otp__box { width: 40px; height: 50px; font-size: 21px; }
}
@media (prefers-reduced-motion: reduce) {
  .otp, .otp *, .otp *::before { animation: none !important; transition: none !important; }
  .otp__check path { stroke-dashoffset: 0; }
}
</style>
